import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import {
  IConditionNode,
  IRulesetAction,
  IRulesetCreateParams,
  serializeConditionNode,
} from '../../../types/ruleset-types';

interface JsonPreviewProps {
  label: string;
  description: string;
  priority: number;
  isActive: boolean;
  conditions: IConditionNode;
  actions: IRulesetAction[];
}

const JsonPreview: React.FC<JsonPreviewProps> = ({
  label,
  description,
  priority,
  isActive,
  conditions,
  actions,
}) => {
  const [copied, setCopied] = useState(false);

  const buildPayload = (): IRulesetCreateParams => {
    return {
      label,
      description: description || undefined,
      priority,
      is_active: isActive,
      conditions: serializeConditionNode(conditions),
      actions,
    };
  };

  const jsonString = JSON.stringify(buildPayload(), null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Accordion
      sx={{
        mt: 2,
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        borderRadius: '4px !important',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: '#f5f5f5',
          minHeight: 40,
          '& .MuiAccordionSummary-content': { margin: '8px 0' },
        }}
      >
        <Typography variant="body2" fontWeight={500}>
          JSON Preview
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255,255,255,0.9)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
              }}
              data-cy="btn-copy-json"
            >
              {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              fontSize: '12px',
              fontFamily: 'monospace',
              overflow: 'auto',
              maxHeight: 300,
              borderRadius: '0 0 4px 4px',
            }}
          >
            {jsonString}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default JsonPreview;
